import { createI18n, defaultLocale, resolveLocale, type SupportedLocale, type TranslationParameters } from '@prompt-assistant/i18n';
import type { UILocale } from '@prompt-assistant/contracts';
import { APIClientError } from './api';

const localeStoragePrefix = 'prompt-assistant.ui-locale.v1';
const pendingLocaleStoragePrefix = 'prompt-assistant.ui-locale-pending.v1';
let preference: UILocale = readCachedPreference();

function localeStorageKey(accountID = 'anonymous'): string { return `${localeStoragePrefix}.${accountID}`; }
function pendingLocaleStorageKey(accountID: string): string { return `${pendingLocaleStoragePrefix}.${accountID}`; }
function systemLocale(): SupportedLocale { return resolveLocale(typeof navigator === 'undefined' ? defaultLocale : navigator.languages, defaultLocale); }
function isUILocale(value: unknown): value is UILocale { return value === 'system' || value === 'zh-CN' || value === 'en-US'; }

function readCachedPreference(accountID = 'anonymous'): UILocale {
  try {
    const value = localStorage.getItem(localeStorageKey(accountID));
    return isUILocale(value) ? value : 'system';
  } catch { return 'system'; }
}

function writeCachedPreference(value: UILocale, accountID = 'anonymous'): void {
  try { localStorage.setItem(localeStorageKey(accountID), value); } catch { /* local cache is an enhancement */ }
}

const initialLocale = preference === 'system' ? systemLocale() : preference;
const i18n = createI18n(initialLocale);
if (typeof document !== 'undefined') document.documentElement.lang = initialLocale;

export function applyLocalePreference(value: UILocale, accountID?: string): SupportedLocale {
  preference = value;
  if (accountID) writeCachedPreference(value, accountID);
  const resolved = value === 'system' ? systemLocale() : value;
  i18n.setLocale(resolved);
  if (typeof document !== 'undefined') document.documentElement.lang = resolved;
  return resolved;
}

export function cachedLocalePreference(accountID?: string): UILocale { return readCachedPreference(accountID); }
export function clearCachedLocalePreference(accountID = 'anonymous'): void {
  try { localStorage.removeItem(localeStorageKey(accountID)); } catch { /* local cache is an enhancement */ }
}
export function pendingLocalePreference(accountID: string): UILocale | null {
  try { const value = localStorage.getItem(pendingLocaleStorageKey(accountID)); return isUILocale(value) ? value : null; } catch { return null; }
}
export function markLocalePreferencePending(value: UILocale, accountID: string): void {
  try { localStorage.setItem(pendingLocaleStorageKey(accountID), value); } catch { /* retry state is best-effort */ }
}
export function clearPendingLocalePreference(accountID: string): void {
  try { localStorage.removeItem(pendingLocaleStorageKey(accountID)); } catch { /* retry state is best-effort */ }
}
export function currentLocale(): SupportedLocale { return i18n.locale; }
export function currentLocalePreference(): UILocale { return preference; }
export function t(key: string, params?: TranslationParameters): string { return i18n.t(key, params); }
export function formatDate(value: string | number | Date, options?: Intl.DateTimeFormatOptions): string { return i18n.formatDate(value, options); }
export function languageName(value: string): string { return i18n.languageName(value); }

const errorKeys: Record<string, string> = {
  AUTH_REQUIRED: 'auth.required', INVALID_CREDENTIALS: 'auth.invalidCredentials', SESSION_REVOKED: 'auth.sessionRevoked',
  INVALID_JSON: 'request.invalid', CSRF_INVALID: 'request.forbidden', FORBIDDEN: 'request.forbidden', NOT_FOUND: 'request.notFound',
  VERSION_CONFLICT: 'request.conflict', RATE_LIMITED: 'request.rateLimited', PREFERENCE_LOCALE_INVALID: 'request.invalid',
  GUEST_CLAIM_FAILED: 'auth.claimFailed', GUEST_ACCOUNT_REQUIRED: 'auth.guestUnavailable', DEVICE_LIMIT: 'auth.deviceLimit'
};

export function localizedErrorText(error: unknown): string {
  if (error instanceof APIClientError) {
    const key = error.payload.code ? errorKeys[error.payload.code] : undefined;
    return key ? t(key) : t('request.failed', { status: error.status });
  }
  if (error instanceof TypeError) return t('request.network');
  return t('request.failed', { status: 'unknown' });
}
